import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import {
    makeRequest,
    axiosOrgInstance,
    axiosV2APIInstance,
    axiosDotNetOrgInstance,
    type ApiEnvelope,
} from './middleware';
import { sortLocationTree } from '@/lib/location-order';
import { ServiceLevel, meetsServiceLevel } from '@/lib/service-level';

import type {
    Contact,
    Group,
    GroupForm,
    OrganizationData,
    RequestType,
    Role,
    User,
    Toast,
    ToastType,
    UserWithContacts,
    ContactType,
    OrganizationContactFormData,
    AdminContract,
    VisitorById,
    VisitorByOrgId,
    ReportOverviewResponse,
    RequestTypeReport,
    CrossConnectionsOverviewResponse,
    BMSDataResponse,
    GenericInfoCard,
    RawBMSDataResponse,
    UUIDLocation,
    AdminUserImpersonationRequest,
    OrganizationUserInvitation,
    DotNetUserInvitationDto,
    AccessCard,
    Region,
    Site,
    Building,
    Room,
    Pod,
    RackRow,
    Rack,
} from './store-interfaces';
import {
    getCPRolesFromToken,
    isVisitor,
    selectActiveOrganization,
    translateAssociationArray,
    translateAssociationItem,
    hasGeneralDataAccess,
    parseUnixSeconds,
    mapDotNetRequestType,
    type DotNetRequestType,
} from './helpers';
import { jwtDecode } from 'jwt-decode';
import { getEnv } from '@/config/projectEnvVariables';

type DotNetResponseEnvelope<T> = {
    statusCode: number;
    message?: string;
    detail?: string;
    data?: T;
};

/** What each `associationTo` segment of the association endpoints returns. */
type AssociationTargets = {
    users: User[];
    groups: Group[];
    roles: Role[];
    requesttypes: RequestType[];
    regions: Region[];
    sites: Site[];
    buildings: Building[];
    rooms: Room[];
    pods: Pod[];
    rack_rows: RackRow[];
    racks: Rack[];
};

type CreateUserInvitationResult = { outcome: 'PENDING' | 'ADDED' };

function requireDotNetData<T>(envelope: DotNetResponseEnvelope<T> | undefined, what: string): T {
    if (!envelope?.data) {
        throw new Error(envelope?.detail || envelope?.message || `${what}: empty response`);
    }
    return envelope.data;
}

type UserImpersonationDecision = 'approved' | 'declined';
type UserInvitationsResponse = {
    pending: OrganizationUserInvitation[];
    recentlyProcessed: OrganizationUserInvitation[];
};

function mapDotNetUserInvitation(dto: DotNetUserInvitationDto): OrganizationUserInvitation {
    return {
        user_invitation_id: dto.userInvitationId,
        email: dto.email ?? '',
        first_name: dto.firstName ?? '',
        last_name: dto.lastName ?? '',
        organization_id: dto.organizationId ?? 0,
        organization:
            dto.organizationId !== null && dto.organizationName !== null
                ? { organization_id: dto.organizationId, name: dto.organizationName }
                : undefined,
        status: dto.status,
        created_at: parseUnixSeconds(dto.createdAt),
        modified_at: parseUnixSeconds(dto.modifiedAt),
        created_by: dto.createdBy,
        creator: dto.creator
            ? {
                  ID: dto.creator.id,
                  FIRST_NAME: dto.creator.firstName ?? '',
                  LAST_NAME: dto.creator.lastName ?? '',
                  EMAIL: dto.creator.email ?? '',
                  USERNAME: dto.creator.username ?? '',
                  ENABLED: dto.creator.enabled,
              }
            : null,
        modified_by: null,
        groups: dto.groups ?? [],
        roles: dto.roles ?? [],
    };
}

export type AssociationEntity = User | Group | Role | RequestType | Region | Site | Building | Room | Pod | RackRow | Rack;

export const useOrganizationStore = defineStore('organization', () => {
    const usersOrganization = ref<OrganizationData>();
    const user = ref<User>();
    const availableOrganizations = ref<OrganizationData[]>([]);
    const toastMessage = ref<Toast[]>([]);
    const token = localStorage.getItem('token');
    const tokenExpires = localStorage.getItem('tokenExpires');
    const currentUserRoles = computed(
        () =>
            (user.value?.organizations?.find((org) => org.organization_id === usersOrganization.value?.organization_id)
                ?.organization_roles || []) as Role[],
    );
    const currentOrgRoles = computed(() => (usersOrganization.value?.organization_roles || []) as Role[]);
    const isOrgAdmin = computed(() => currentUserRoles.value.some((role) => role.name === 'org-admin'));
    // Raw BMS sensor data is reserved for Silver organizations and above (ITDEVELOP-3540).
    const canViewRawSensorData = computed(() =>
        meetsServiceLevel(usersOrganization.value?.service_level_id, ServiceLevel.Silver),
    );
    const hasDataAccess = computed(
        () =>
            hasGeneralDataAccess(currentUserRoles.value) ||
            (isOrgAdmin.value && hasGeneralDataAccess(usersOrganization.value?.organization_roles || [])),
    );

    // Startup runs fire-and-forget, so early requests race the organization list —
    // await this promise before anything that derives X-Organization-Id from the store.
    const startupComplete = startup();

    async function startup() {
        if (
            token === null ||
            tokenExpires === null ||
            new Date(Number(localStorage.getItem('tokenExpires')) * 1000).getTime() < Date.now()
        ) {
            return;
        }

        const userCPRoles = getCPRolesFromToken(token);
        if (!isVisitor(userCPRoles)) {
            await makeRequest<ApiEnvelope<OrganizationData[]>>(
                'GET',
                'organization-management/organizations',
                axiosOrgInstance,
            ).then(
                async (response) => {
                    availableOrganizations.value = response.data?.data;
                    if (availableOrganizations.value?.length > 0) {
                        await populateOrganizations(); // this needs to be before populate user
                        await populateUser();
                    }
                },
            );
        }
    }

    async function populateOrganizations() {
        const storedOrganizationId = localStorage.getItem('organization_id');

        // Honour an explicit prior selection; otherwise default to atNorth when available rather
        // than an arbitrary first entry of the unordered org list (ITDEVELOP-2919).
        usersOrganization.value = selectActiveOrganization(availableOrganizations.value, storedOrganizationId);

        if (usersOrganization.value) {
            localStorage.setItem('organization_id', usersOrganization.value.organization_id.toString());
        } else {
            localStorage.removeItem('organization_id');
        }
    }

    async function populateUser() {
        if (token !== null) {
            const decodedToken = jwtDecode(token) as { sub: string };
            user.value = await getUserManagementUser(decodedToken.sub);
        } else console.error('No token found');
    }

    async function getUserManagementUser(userId: string) {
        const response = await makeRequest<ApiEnvelope<User>>(
            'GET',
            `user-management/users/${userId}`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getOrganizationById(id: string) {
        const response = await makeRequest<ApiEnvelope<OrganizationData>>(
            'GET',
            `organization-management/organizations/${id}`,
            axiosOrgInstance,
        );
        return response.data;
    }

    async function getAllOrganizations() {
        const response = await makeRequest<ApiEnvelope<OrganizationData[]>>(
            'GET',
            `organization-management/organizations`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getUsers(serviceDesk?: boolean) {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        let url = `organization-management/organizations/${usersOrganization.value?.organization_id}/users`;
        if (serviceDesk) {
            url += '?servicedesk=true';
        }
        const response = await makeRequest<ApiEnvelope<User[]>>('GET', url, axiosOrgInstance);
        return response.data.data;
    }

    async function getUsersWithContacts() {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        const response = await makeRequest<ApiEnvelope<UserWithContacts[]>>(
            'GET',
            `organization-management/organizations/${usersOrganization.value?.organization_id}/users?contact=true`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getRoles() {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        const response = await makeRequest<ApiEnvelope<Role[]>>(
            'GET',
            `organization-management/organizations/${usersOrganization.value?.organization_id}/roles`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getRole(roleId: number) {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        const response = await makeRequest<ApiEnvelope<Role>>(
            'GET',
            `organization-management/organizations/${usersOrganization.value?.organization_id}/roles/${roleId}`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getUsersRoles(userId: string) {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        const response = await makeRequest<ApiEnvelope<Role[]>>(
            'GET',
            `organization-management/organizations/${usersOrganization.value?.organization_id}/users/${userId}/roles`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getUser(userId: string) {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        const response = await makeRequest<DotNetResponseEnvelope<DotNetUser>>(
            'GET',
            `/users/${userId}`,
            axiosDotNetOrgInstance,
        );
        return mapDotNetUser(requireDotNetData(response.data, 'getUser'));
    }

    async function getUserImpersonationRequests() {
        const response = await makeRequest<DotNetResponseEnvelope<AdminUserImpersonationRequest[]>>(
            'GET',
            'users/impersonations',
            axiosDotNetOrgInstance,
            undefined,
            {
                throwOnError: true,
                skipToast: true,
            },
        );
        return response.data.data ?? [];
    }

    async function manageUserImpersonationRequest(userImpersonationId: string, decision: UserImpersonationDecision) {
        const response = await makeRequest<DotNetResponseEnvelope<AdminUserImpersonationRequest>>(
            'PATCH',
            `users/impersonations/${userImpersonationId}`,
            axiosDotNetOrgInstance,
            { decision },
            {
                throwOnError: true,
                skipToast: true,
            },
        );
        return response.data.data;
    }

    async function getGroup(groupId: number) {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        const response = await makeRequest<ApiEnvelope<Group>>(
            'GET',
            `organization-management/organizations/${usersOrganization.value?.organization_id}/groups/${groupId}`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getGroups() {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        const response = await makeRequest<ApiEnvelope<Group[]>>(
            'GET',
            `organization-management/organizations/${usersOrganization.value?.organization_id}/groups`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getUsersGroups(userId: string) {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        const response = await makeRequest<ApiEnvelope<Group[]>>(
            'GET',
            `organization-management/organizations/${usersOrganization.value?.organization_id}/users/${userId}/groups`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    // Location trees are ordered by name at every level here, right after the fetch, so no
    // consumer has to sort sites or footprints itself (ITDEVELOP-3093).
    async function getLocations() {
        const response = await makeRequest<ApiEnvelope<UUIDLocation[]>>('GET', `locations`, axiosDotNetOrgInstance);

        return sortLocationTree(response.data.data);
    }

    async function getLocationsByUserId(userID: string): Promise<UUIDLocation[]> {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        const response = await makeRequest<ApiEnvelope<UUIDLocation[]>>(
            'GET',
            `/users/${userID}/locations`,
            axiosDotNetOrgInstance,
        );
        return sortLocationTree(response.data.data);
    }

    async function getLocationsByGroupUUID(groupUUID: string): Promise<UUIDLocation[]> {
        const response = await makeRequest<ApiEnvelope<UUIDLocation[]>>(
            'GET',
            `groups/${groupUUID}/locations`,
            axiosDotNetOrgInstance,
        );
        return sortLocationTree(response.data.data);
    }

    async function getLocation(locationUUID: string) {
        const response = await makeRequest<ApiEnvelope<UUIDLocation>>(
            'GET',
            `/locations/${locationUUID}`,
            axiosDotNetOrgInstance,
        );
        const [location] = sortLocationTree([response.data.data]);
        return location;
    }

    async function getHistoricData(
        dataType: string,
        site: string,
        startDate?: string,
        endDate?: string,
        zone?: string,
        offset?: number,
        interval?: string,
        aggregate?: string,
    ) {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');

        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate as string);
        if (endDate) params.append('endDate', endDate as string);
        if (zone && zone.includes(':')) params.append('zone', zone as string);
        if (interval) {
            params.append('interval', interval);
        } else {
            params.append('interval', 'auto');
        }
        if (offset !== undefined) {
            params.append('pagesize', '50');

            params.append('offset', offset.toString());
        } else {
            params.append('pagesize', '0');
        }
        if (aggregate) params.append('aggregate', aggregate);
        if (dataType === 'pue') {
            dataType = 'environment';
        }
        const baseUrl = getEnv().VITE_TELEMETRY_API_HOST;
        const url = baseUrl + `/dc/${site}/sensors/${dataType}/historic?${params.toString()}`;
        const response = await makeRequest<RawBMSDataResponse | BMSDataResponse>('GET', url, axiosV2APIInstance);

        if (aggregate === 'raw') {
            return response.data as RawBMSDataResponse;
        } else {
            return response.data as BMSDataResponse;
        }
    }

    async function getLiveData(dataType: string, site: string, zone?: string) {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        if (dataType === 'pue') {
            dataType = 'environment';
        }
        const baseUrl = getEnv().VITE_TELEMETRY_API_HOST;
        let url = baseUrl + `/dc/${site}/sensors/${dataType}/current`;
        if (zone) {
            url += `?zone=${zone}`;
        }

        const response = await makeRequest<ApiEnvelope<GenericInfoCard[]>>('GET', url, axiosV2APIInstance);
        return response.data.data;
    }

    async function getAssociation<K extends keyof AssociationTargets>(
        associationFrom: string,
        associationFromId: string,
        associationTo: K,
        queryParams?: Record<string, string | number>,
    ) {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');

        const search = queryParams ? '?' + new URLSearchParams(
            Object.entries(queryParams).map(([k, v]) => [k, String(v)]),
        ).toString() : '';

        const response = await makeRequest<ApiEnvelope<AssociationTargets[K]>>(
            'GET',
            `organization-management/organizations/${usersOrganization.value?.organization_id}/${associationFrom}/${associationFromId}/${associationTo}${search}`,
            axiosOrgInstance,
        );

        return response.data.data;
    }

    async function getDotNetAssociation<K extends keyof AssociationTargets>(
        associationFrom: string,
        associationFromId: string,
        associationTo: K,
    ) {
        const response = await makeRequest<ApiEnvelope<AssociationTargets[K]>>(
            'GET',
            `${associationFrom}/${associationFromId}/${associationTo}`,
            axiosDotNetOrgInstance,
        );

        return response.data.data;
    }

    async function addAssociation(
        associationFrom: string,
        associationFromId: string,
        associationTo: string,
        data: AssociationEntity[],
    ) {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        const translatedData = translateAssociationArray(associationTo, data);
        const response = await makeRequest(
            'PUT',
            `organization-management/organizations/${usersOrganization.value?.organization_id}/${associationFrom}/${associationFromId}/${associationTo}`,
            axiosOrgInstance,
            translatedData,
        );

        return response.data;
    }

    async function associateLocation(
        associationFrom: string,
        associationFromId: string,
        associationTo: string,
        items: Array<{ locationId: string }>,
    ) {
        const response = await makeRequest(
            'POST',
            `${associationFrom}/${associationFromId}/${associationTo}`,
            axiosDotNetOrgInstance,
            items,
        );
        return response.data;
    }

    async function removeLocationAssociation(
        associationFrom: string,
        associationFromId: string,
        associationTo: string,
        items: Array<{ locationId: string }>,
    ) {
        const response = await makeRequest(
            'DELETE',
            `${associationFrom}/${associationFromId}/${associationTo}`,
            axiosDotNetOrgInstance,
            items,
        );
        return response.data;
    }

    async function removeAssociation(
        associationFrom: string,
        associationFromId: string,
        associationTo: string,
        data: AssociationEntity,
    ) {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        const translatedData = translateAssociationItem(associationTo, data);
        const response = await makeRequest(
            'DELETE',
            `organization-management/organizations/${usersOrganization.value?.organization_id}/${associationFrom}/${associationFromId}/${associationTo}`,
            axiosOrgInstance,
            translatedData,
        );

        return response.data;
    }

    async function createGroup(GroupFormArray: Array<GroupForm>) {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        const response = await makeRequest<ApiEnvelope<Group[]>>(
            'POST',
            `organization-management/organizations/${usersOrganization.value?.organization_id}/groups`,
            axiosOrgInstance,
            GroupFormArray,
        );

        return response.data.data;
    }

    async function deleteGroup(ArrayOfObjectIds: Array<object>) {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        const response = await makeRequest(
            'DELETE',
            `organization-management/organizations/${usersOrganization.value?.organization_id}/groups`,
            axiosOrgInstance,
            ArrayOfObjectIds,
        );

        return response.data;
    }

    async function getGenericData() {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        const response = await makeRequest<ApiEnvelope<Record<string, number>>>(
            'GET',
            `organization-management/organizations/${usersOrganization.value?.organization_id}/generalinfo`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getRequestTypes(requestTypeType?: number) {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        const search = requestTypeType !== undefined ? `?requestTypeType=${requestTypeType}` : '';
        const response = await makeRequest<ApiEnvelope<RequestType[]>>(
            'GET',
            `organization-management/organizations/${usersOrganization.value?.organization_id}/requesttypes${search}`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getDotNetRequestTypes(requestTypeType?: number) {
        const search = requestTypeType !== undefined ? `?requestTypeType=${requestTypeType}` : '';
        const response = await makeRequest<ApiEnvelope<DotNetRequestType[]>>(
            'GET',
            `requesttypes${search}`,
            axiosDotNetOrgInstance,
        );
        const list = response.data?.data ?? [];
        return list.map(mapDotNetRequestType);
    }

    async function getDotNetGroupRequestTypes(groupUuid: string, requestTypeType?: number) {
        const search = requestTypeType !== undefined ? `?requestTypeType=${requestTypeType}` : '';
        const response = await makeRequest<ApiEnvelope<DotNetRequestType[]>>(
            'GET',
            `groups/${groupUuid}/request-types${search}`,
            axiosDotNetOrgInstance,
        );
        const list = response.data?.data ?? [];
        return list.map(mapDotNetRequestType);
    }

    async function addDotNetGroupRequestTypes(groupUuid: string, requestTypeUuids: string[]) {
        const response = await makeRequest(
            'POST',
            `groups/${groupUuid}/request-types`,
            axiosDotNetOrgInstance,
            requestTypeUuids.map((id) => ({ requestTypeId: id })),
        );
        return response.data;
    }

    async function removeDotNetGroupRequestTypes(groupUuid: string, requestTypeUuids: string[]) {
        const response = await axiosDotNetOrgInstance.delete(`groups/${groupUuid}/request-types`, {
            data: requestTypeUuids.map((id) => ({ requestTypeId: id })),
        });
        return response.data;
    }

    async function getRequestType(requestTypeId: string) {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        const response = await makeRequest<ApiEnvelope<RequestType>>(
            'GET',
            `organization-management/organizations/${usersOrganization.value?.organization_id}/requesttypes/${requestTypeId}`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function updatePassword(userId: string) {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        const response = await makeRequest(
            'PUT',
            `organization-management/organizations/${usersOrganization.value?.organization_id}/users/actions/update_password`,
            axiosOrgInstance,
            [{ user_id: userId }],
        );
        return response.data;
    }

    async function inviteUser(userForm: UserInvite) {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        const response = await makeRequest<DotNetResponseEnvelope<CreateUserInvitationResult>>(
            'POST',
            `users/invitations`,
            axiosDotNetOrgInstance,
            {
                email: userForm.email,
                firstName: userForm.first_name,
                lastName: userForm.last_name,
                groupIds: userForm.groups.map((option) => Number(option.value)),
                roleIds: userForm.roles.map((option) => Number(option.value)),
            },
            {
                throwOnError: true,
                skipToast: true,
            },
        );
        return response.data.data;
    }

    async function getPendingUserInvitations(): Promise<UserInvitationsResponse> {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        const response = await makeRequest<
            ApiEnvelope<{ pending: DotNetUserInvitationDto[]; recentlyProcessed: DotNetUserInvitationDto[] }>
        >(
            'GET',
            `users/invitations`,
            axiosDotNetOrgInstance,
            undefined,
            {
                throwOnError: true,
                skipToast: true,
            },
        );
        const { pending = [], recentlyProcessed = [] } = response.data.data;

        return {
            pending: pending.map(mapDotNetUserInvitation),
            recentlyProcessed: recentlyProcessed.map(mapDotNetUserInvitation)
        };
    }

    async function deleteUserInvitation(invitationId: number) {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        const response = await makeRequest(
            'PUT',
            `users/invitations/${invitationId}/cancel`,
            axiosDotNetOrgInstance,
            undefined,
            {
                throwOnError: true,
                skipToast: true,
            },
        );
        return response.data;
    }

    async function deleteUser(userId: string) {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        const response = await makeRequest(
            'DELETE',
            `organization-management/organizations/${usersOrganization.value?.organization_id}/users/${userId}`,
            axiosOrgInstance,
            undefined,
            {
                throwOnError: true,
                skipToast: true,
            },
        );
        return response.data;
    }

    async function getContactsByOrgId(orgId: string) {
        const response = await makeRequest<ApiEnvelope<Contact[]>>(
            'GET',
            `organization-management/organizations/${orgId}/contacts`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getAllContactTypes(orgId: number) {
        const response = await makeRequest<ApiEnvelope<ContactType[]>>(
            'GET',
            `organization-management/organizations/${orgId}/contacts/types`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function assignContacts(body: OrganizationContactFormData[]) {
        const response = await makeRequest(
            'PUT',
            `organization-management/organizations/${usersOrganization.value?.organization_id}/contacts`,
            axiosOrgInstance,
            body,
        );
        return response.data;
    }

    async function removeContactTypeFromUser(userId: string, contactId: number) {
        // contactId is the id of the contact PER type... so one user can have multiple contactId's
        const response = await makeRequest(
            'DELETE',
            `organization-management/organizations/${usersOrganization.value?.organization_id}/contacts/${contactId}`,
            axiosOrgInstance,
        );

        return response;
    }

    async function getSLAValuesByOrgId(orgId: number) {
        const response = await makeRequest<ApiEnvelope<AdminContract[]>>(
            'GET',
            `organization-management/organizations/${orgId}/contracts`,
            axiosOrgInstance,
        );
        return response.data.data[0];
    }

    // Visitors

    /**
     *
     * @param securityStatus Can be undefined, 0 or 1,
     * if undefined, all visitors are returned,
     * if 0 only visitors with security status 'Incomplete' are returned,
     * if 1 only visitors with security status 'Completed' are returned
     * @returns list of vistors
     */
    async function getVisitorsByOrgId(securityStatus: null | 0 | 1) {
        const response = await makeRequest<ApiEnvelope<VisitorByOrgId[]>>(
            'GET',
            `organization-management/organizations/${localStorage.getItem('organization_id')}/visitors${
                securityStatus ? '?securityStatus=' + securityStatus : ''
            }`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getVisitorById(visitorId: string) {
        const response = await makeRequest<ApiEnvelope<VisitorById>>(
            'GET',
            `organization-management/organizations/${localStorage.getItem('organization_id')}/visitors/${visitorId}`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function deleteVisitorById(visitorId: string) {
        const response = await makeRequest(
            'DELETE',
            `organization-management/organizations/${localStorage.getItem('organization_id')}/visitors/${visitorId}`,
            axiosOrgInstance,
        );
        return response.status === 204;
    }

    async function postVisitorById(body: VisitorCreationBody): Promise<boolean> {
        const response = await makeRequest(
            'POST',
            `organization-management/organizations/${localStorage.getItem('organization_id')}/visitors`,
            axiosOrgInstance,
            [body],
        );

        return response.status === 201;
    }

    async function patchVisitor(
        id: string,
        body: Omit<VisitorCreationBody, 'email' | 'organizations'>,
    ): Promise<boolean> {
        const response = await makeRequest(
            'PATCH',
            `organization-management/organizations/${localStorage.getItem('organization_id')}/visitors/${id}`,
            axiosOrgInstance,
            body,
        );
        return response.status === 200;
    }

    async function getReportsByReportTypeUUID(reportTypeUUID: string) {
        const baseUrl = getEnv().VITE_INTEGRATION_API_HOST;

        const response = await makeRequest<ReportOverviewResponse>(
            'GET',
            baseUrl + `/servicedesk/requesttypes/${reportTypeUUID}/reports`,
            axiosV2APIInstance,
        );
        return response.data;
    }

    async function getReportById(reportUUID: string, reportTypeUUID: string, pageSize?: number, offset?: number) {
        const baseUrl = getEnv().VITE_INTEGRATION_API_HOST;
        const response = await makeRequest<RequestTypeReport>(
            'GET',
            baseUrl +
                `/servicedesk/requesttypes/${reportTypeUUID}/reports/${reportUUID}${
                    pageSize ? `?pageSize=${pageSize}` : ''
                }${offset ? `&offset=${offset}` : ''}`,
            axiosV2APIInstance,
        );
        return response.data;
    }

    async function getAccessCards() {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        const response = await makeRequest<{ data: AccessCard[] }>(
            'GET',
            `organization-management/organizations/${usersOrganization.value?.organization_id}/accesscards`,
            axiosOrgInstance,
        );

        // map all unix timestamps into Date
        const mapped = {
            ...response.data,
            data: [
                ...response.data.data.map((item) => {
                    return {
                        ...item,
                        start_date: parseUnixSeconds(item.start_date),
                        end_date: item.end_date != null ? parseUnixSeconds(item.end_date) : null,
                    };
                }),
            ],
        };

        return mapped.data as unknown as AccessCard[];
    }

    async function getCrossConnections(pageSize?: number, offset?: number) {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        const baseUrl = getEnv().VITE_INTEGRATION_API_HOST;
        let url = baseUrl + '/dcim/connections';
        const params: string[] = [];
        if (pageSize !== undefined) params.push(`pageSize=${pageSize}`);
        if (offset !== undefined) params.push(`offset=${offset}`);
        if (params.length > 0) url += `?${params.join('&')}`;
        const response = await makeRequest<CrossConnectionsOverviewResponse>('GET', url, axiosV2APIInstance);
        return response.data;
    }

    async function updateUserDetails(userId: string, body: UserDetailsUpdatePayload) {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        const response = await makeRequest<DotNetResponseEnvelope<DotNetUser>>(
            'PATCH',
            `/users/${userId}`,
            axiosDotNetOrgInstance,
            body,
            { throwOnError: true, skipToast: true },
        );
        return mapDotNetUser(requireDotNetData(response.data, 'updateUserDetails'));
    }

    async function toggleUser(userId: string, enabled: boolean) {
        if (usersOrganization.value === undefined) throw new Error('No organization selected');
        const response = await makeRequest(
            'PUT',
            `organization-management/organizations/${usersOrganization.value?.organization_id}/users/${userId}`,
            axiosOrgInstance,
            { enabled: enabled },
        );
        return response;
    }

    function setToastMessage(message: string, type: ToastType) {
        const index = toastMessage.value.length;
        toastMessage.value?.push({ message, type, index });
    }

    function clearToastMessage() {
        toastMessage.value.shift();
    }

    return {
        // Refs
        startupComplete,
        usersOrganization,
        user,
        availableOrganizations,
        toastMessage,
        currentUserRoles,
        currentOrgRoles,
        isOrgAdmin,
        hasDataAccess,
        canViewRawSensorData,

        //functions
        populateOrganizations,
        populateUser,
        getOrganizationById,
        getAllOrganizations,
        getUsers,
        getUsersWithContacts,
        getUser,
        getUserImpersonationRequests,
        manageUserImpersonationRequest,
        getUserManagementUser,
        getRoles,
        getRole,
        getUsersRoles,
        getGroup,
        getGroups,
        getUsersGroups,
        getHistoricData,
        getLiveData,
        createGroup,
        deleteGroup,
        getLocationsByGroupUUID,
        getLocation,
        getLocations,
        getLocationsByUserId,
        getAssociation,
        getDotNetAssociation,
        addAssociation,
        associateLocation,
        removeLocationAssociation,
        removeAssociation,
        getGenericData,
        getRequestType,
        getRequestTypes,
        getDotNetRequestTypes,
        getDotNetGroupRequestTypes,
        addDotNetGroupRequestTypes,
        removeDotNetGroupRequestTypes,
        setToastMessage,
        clearToastMessage,
        updatePassword,
        inviteUser,
        getPendingUserInvitations,
        deleteUserInvitation,
        deleteUser,
        getContactsByOrgId,
        getAllContactTypes,
        removeContactTypeFromUser,
        getVisitorsByOrgId,
        getVisitorById,
        deleteVisitorById,
        postVisitorById,
        patchVisitor,
        getSLAValuesByOrgId,
        getReportsByReportTypeUUID,
        getAccessCards,
        getCrossConnections,
        getReportById,
        assignContacts,
        updateUserDetails,
        toggleUser,
    };
});

import type { UserInvite } from '@/views/manager/user-inv.vue';
import type { VisitorCreationBody } from '@/views/visitors/create-visitor-view.vue';
import { getDefaultErrorMessage } from '@/lib/http-error';

export interface UserDetailsUpdatePayload {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string | null;
}

interface DotNetUser {
    id: string;
    username: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    emailVerified: boolean;
    enabled: boolean;
    phoneNumber: string | null;
    created: string | null;
    rolesCount: number;
    groupsCount: number;
    highestRole: string | null;
}

function mapDotNetUser(user: DotNetUser): User {
    const createdAt = user.created ? Date.parse(user.created) : 0;

    return {
        ID: user.id,
        USERNAME: user.username,
        FIRST_NAME: user.firstName ?? '',
        LAST_NAME: user.lastName ?? '',
        EMAIL: user.email ?? '',
        EMAIL_CONSTRAINT: user.email ?? '',
        EMAIL_VERIFIED: user.emailVerified,
        CREATED_AT: Number.isNaN(createdAt) ? 0 : createdAt,
        ENABLED: user.enabled,
        FEDERATION_LINK: '',
        NOT_BEFORE: false,
        REALM_ID: '',
        SERVICE_ACCOUNT_CLIENT_LINK: '',
        PHONE_NUMBER: user.phoneNumber ?? '',
        SYSTEM_ROLE: user.highestRole ?? '',
        roles_count: user.rolesCount,
        locations_count: 0,
        regions_count: 0,
        sites_count: 0,
        buildings_count: 0,
        rooms_count: 0,
        pods_count: 0,
        groups_count: user.groupsCount,
        courses_count: 0,
        organizations: [],
        user_id: user.id,
    };
}

function defaultErrorMessage(error: unknown): string {
    return getDefaultErrorMessage(error);
}

export function useMutationAction() {
    // Specifically for organizationStore now, expand to all stores later
    // this useMutationAction guarantees that you only get 1 toast message
    // either the one you provided, or the default ones
    const orgStore = useOrganizationStore();

    return async function runMutation<T>(
        fn: () => Promise<T>,
        opts: {
            successMessage?: string;
            errorMessage?: string | ((err: unknown) => string);
            successType?: 'check' | 'warning' | 'error';
            errorType?: 'check' | 'warning' | 'error';
            onSuccess?: (result: T) => void | Promise<void>;
            onError?: (err: unknown) => void | Promise<void>;
        } = {},
    ): Promise<T> {
        try {
            const result = await fn();
            if (opts.successMessage) {
                orgStore.setToastMessage(opts.successMessage, opts.successType ?? 'check');
            }
            await opts.onSuccess?.(result);
            return result;
        } catch (err) {
            const msg =
                typeof opts.errorMessage === 'function'
                    ? opts.errorMessage(err)
                    : (opts.errorMessage ?? defaultErrorMessage(err));

            orgStore.setToastMessage(msg, opts.errorType ?? 'warning');
            await opts.onError?.(err);
            throw err;
        }
    };
}
